"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/shadcnComponents/ui/button";
import { Input } from "@/shadcnComponents/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcnComponents/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcnComponents/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcnComponents/ui/select";
import { Download, Plus, CalendarIcon, Loader } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/shadcnComponents/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcnComponents/ui/popover";
import { cn } from "@/lib/utils";
import { useBrand } from "@/Providers/Brand";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { useGeneralSettings } from "@/Providers/General";
import { SidebarTrigger } from "@/shadcnComponents/ui/sidebar";

const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

type transaction = {
  id: number;
  brandName: string;
  campaignName: string;
  amount: string;
  createdAt: string;
  transactionId: string;
  status: string;
  invoiceNumber: string | null;
  invoiceAdded: boolean;
  invoiceAddedAt: string | null;
  invoice: string;
};

export default function AdminBrandInvoicePage() {
  const brandState = useBrand();
  const generalState = useGeneralSettings();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState<transaction[]>([]);
  const [loading, setLoading] = useState(false);

  const [currentTransactionId, setCurrentTransactionId] = useState<
    number | null
  >(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [filteredTransactions, setFilteredTransactions] =
    useState(transactions);
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<string | null>(
    null
  );

  const getTransactions = useCallback(async () => {
    try {
      const response = await fetch(
        `${host}/developer/brand/getAllBrandInvoices`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "Access-Control-Allow-Credentials": "true",
            jwtToken: localStorage.getItem("jwtTokenD"),
          },
        }
      );
      const json = await response.json();
      if (json.success) {
        setTransactions(json?.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleAddInvoice = (transactionId: number) => {
    setInvoiceNumber("");
    setInvoiceFile(null);
    setCurrentTransactionId(transactionId);
    setIsDialogOpen(true);
  };

  const uploadBrandInvoice = async () => {
    if (invoiceFile instanceof Blob) {
      const formData = new FormData();
      formData.append("file", invoiceFile);
      const result = await brandState?.uploadBrandInvoices(formData);
      return result?.success ? result?.result?.Location : invoiceFile;
    }
    return null;
  };

  const handleSubmitInvoice = async () => {
    setLoading(true);
    if (!invoiceNumber) {
      setLoading(false);
      return toast({
        title: "Please enter invoice number",
        variant: "destructive",
      });
    }

    if (!invoiceFile) {
      setLoading(false);
      return toast({
        title: "Please select invoice file",
        variant: "destructive",
      });
    }

    const invoiceLink = await uploadBrandInvoice();

    if (typeof invoiceLink !== "string") {
      setLoading(false);
      return toast({
        title: "Error uploading invoice",
        variant: "destructive",
      });
    }

    const response = await fetch(`${host}/developer/brand/brandInvoice`, {
      method: "POST",
      body: JSON.stringify({
        orderId: currentTransactionId,
        invoiceNumber,
        invoiceLink,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Access-Control-Allow-Credentials": "true",
        jwtToken: localStorage.getItem("jwtTokenD"),
      },
    });
    const json = await response.json();
    if (json.success) {
      toast({
        title: "Invoice added successfully",
      });
      getTransactions();
    } else {
      toast({
        title: "Error adding invoice",
        variant: "destructive",
      });
    }

    setIsDialogOpen(false);
    setInvoiceNumber("");
    setInvoiceFile(null);
    setLoading(false);
  };

  useEffect(() => {
    let filtered = transactions;

    if (brandFilter) {
      filtered = filtered.filter(
        (t) => t.brandName === brandFilter || brandFilter === "all"
      );
    }

    if (startDate) {
      filtered = filtered.filter((t) => new Date(t.createdAt) >= startDate);
    }

    if (endDate) {
      filtered = filtered.filter((t) => new Date(t.createdAt) <= endDate);
    }

    if (statusFilter) {
      filtered = filtered.filter(
        (t) => t.status === statusFilter || statusFilter === "all"
      );
    }

    if (invoiceTypeFilter) {
      filtered = filtered.filter(
        (t) =>
          (invoiceTypeFilter === "added" && t.invoiceAdded) ||
          (invoiceTypeFilter === "not_added" && !t.invoiceAdded) ||
          invoiceTypeFilter === "all"
      );
    }

    setFilteredTransactions(filtered);
  }, [
    brandFilter,
    startDate,
    endDate,
    statusFilter,
    invoiceTypeFilter,
    transactions,
  ]);

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  const uniqueBrands = Array.from(
    new Set(transactions.map((t) => t.brandName))
  );

  return (
    <>
      <div className="flex flex-col w-full h-screen">
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-6">
          <SidebarTrigger />
          <h1 className="text-2xl font-bold">Brand Invoices</h1>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <div className="mb-6 flex flex-nowrap gap-2 overflow-x-auto pb-2">
            <Select onValueChange={(value) => setBrandFilter(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select Brand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {uniqueBrands.map((brand) => (
                  <SelectItem key={brand} value={brand}>
                    {brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-[140px] justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? (
                      format(startDate, "PPP")
                    ) : (
                      <span>Start Date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-[140px] justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : <span>End Date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Select onValueChange={(value) => setStatusFilter(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select onValueChange={(value) => setInvoiceTypeFilter(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Invoice Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="added">Added</SelectItem>
                <SelectItem value="not_added">Not Added</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brand Name</TableHead>
                <TableHead>Campaign Name</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Transaction ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invoice Number</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Invoice Added At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransactions?.map((transaction) => (
                <TableRow key={transaction?.id}>
                  <TableCell className=" text-left ">
                    {transaction?.brandName}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction?.campaignName}
                  </TableCell>
                  <TableCell className=" text-left ">
                    ₹{parseFloat(transaction?.amount)?.toFixed(2)}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction?.createdAt}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction?.transactionId}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction.status}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction?.invoiceNumber || "N/A"}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction?.invoiceAdded ? (
                      <Button
                        onClick={() =>
                          generalState?.handleDownloadPdf(transaction?.invoice)
                        }
                        variant="ghost"
                        size="icon"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleAddInvoice(transaction?.id)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className=" text-left ">
                    {transaction?.invoiceAddedAt || "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </main>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Invoice</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="invoiceNumber" className="text-right">
                Invoice Number
              </label>
              <Input
                id="invoiceNumber"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="invoiceFile" className="text-right">
                Invoice File
              </label>
              <Input
                id="invoiceFile"
                type="file"
                accept=".pdf"
                onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                className="col-span-3"
              />
            </div>
          </div>
          <Button onClick={handleSubmitInvoice}>
            {loading ? <Loader className="animate-spin" /> : "Submit"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
